import { useCallback } from "react";

export function useDraftCache(cacheName) {
  const openCache = useCallback(async () => {
    return await caches.open(cacheName);
  }, [cacheName]);

  const get = useCallback(
    async (key, fileKeys = []) => {
      const cache = await openCache();
      const metaResp = await cache.match(key);

      if (!metaResp) {
        return null;
      }

      const metadata = await metaResp.json();

      const files = {};
      for (const fileKey of fileKeys) {
        const fileResp = await cache.match(fileKey);
        if (fileResp) {
          const blob = await fileResp.blob();
          files[fileKey] = new File([blob], fileKey.split("/").pop(), {
            type: blob.type
          });
        }
      }

      return { data: metadata, files };
    },
    [openCache]
  );

  const set = useCallback(
    async (key, data, fileMap = {}) => {
      const cache = await openCache();

      await cache.put(
        new Request(key),
        new Response(JSON.stringify(data), {
          headers: { "Content-Type": "application/json" }
        })
      );

      for (const [fileKey, file] of Object.entries(fileMap)) {
        await cache.put(new Request(fileKey), new Response(file));
      }
    },
    [openCache]
  );

  const remove = useCallback(
    async (keys = []) => {
      const cache = await openCache();
      await Promise.all(keys.map((k) => cache.delete(k)));
    },
    [openCache]
  );

  const update = useCallback(
    async (key, updaterFn) => {
      const cache = await openCache();
      const oldResp = await cache.match(key);
      if (!oldResp) return;

      const oldData = await oldResp.json();
      const newData = updaterFn(oldData);

      await cache.put(
        new Request(key),
        new Response(JSON.stringify(newData), {
          headers: { "Content-Type": "application/json" }
        })
      );
    },
    [openCache]
  );

  return {
    get,
    set,
    update,
    remove
  };
}
